import React, { createContext, useContext, useState, type ReactNode } from 'react';

/**
 * Interfaz para los archivos del registro de entidades
 * Mantiene referencias a objetos File en memoria RAM (sin límites de tamaño)
 */
interface RegistrationFiles {
    /** Logo de la entidad (imagen PNG/JPG) */
    logoFile: File | null;
    /** Documento de representación (PDF/DOC/DOCX) */
    documentoRepresentacionFile: File | null;
    /** Documento de registro (PDF/DOC/DOCX) */
    documentoRegistroFile: File | null;
}

/**
 * Interfaz del contexto
 */
export interface RegistrationFilesContextType {
    /** Archivos almacenados en memoria */
    files: RegistrationFiles;
    /** Establecer el logo de la entidad */
    setLogoFile: (file: File | null) => void;
    /** Establecer el documento de representación */
    setDocumentoRepresentacionFile: (file: File | null) => void;
    /** Establecer el documento de registro */
    setDocumentoRegistroFile: (file: File | null) => void;
    /** Limpiar todos los archivos de la memoria */
    clearAllFiles: () => void;
}

// Crear el contexto
const RegistrationFilesContext = createContext<RegistrationFilesContextType | undefined>(undefined);

/**
 * Provider del contexto de archivos de registro
 * Mantiene archivos en memoria RAM durante el flujo de registro de entidades
 * 
 * @example
 * ```tsx
 * <RegistrationFilesProvider>
 *   <RegistroEntidadFlow />
 * </RegistrationFilesProvider>
 * ```
 */
export const RegistrationFilesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [files, setFiles] = useState<RegistrationFiles>({
        logoFile: null,
        documentoRepresentacionFile: null,
        documentoRegistroFile: null,
    });

    const setLogoFile = (file: File | null) => {
        setFiles(prev => ({ ...prev, logoFile: file }));
        console.log('📁 [RegistrationFilesContext] Logo guardado en memoria:', file?.name, file?.size ? `(${(file.size / 1024).toFixed(2)} KB)` : '');
    };

    const setDocumentoRepresentacionFile = (file: File | null) => {
        setFiles(prev => ({ ...prev, documentoRepresentacionFile: file }));
        console.log('📁 [RegistrationFilesContext] Documento de representación guardado en memoria:', file?.name, file?.size ? `(${(file.size / 1024).toFixed(2)} KB)` : '');
    };

    const setDocumentoRegistroFile = (file: File | null) => {
        setFiles(prev => ({ ...prev, documentoRegistroFile: file }));
        console.log('📁 [RegistrationFilesContext] Documento de registro guardado en memoria:', file?.name, file?.size ? `(${(file.size / 1024).toFixed(2)} KB)` : '');
    };

    const clearAllFiles = () => {
        setFiles({
            logoFile: null,
            documentoRepresentacionFile: null,
            documentoRegistroFile: null,
        });
        console.log('🗑️ [RegistrationFilesContext] Todos los archivos han sido limpiados de la memoria');
    };

    const value: RegistrationFilesContextType = {
        files,
        setLogoFile,
        setDocumentoRepresentacionFile,
        setDocumentoRegistroFile,
        clearAllFiles,
    };

    return (
        <RegistrationFilesContext.Provider value={value}>
            {children}
        </RegistrationFilesContext.Provider>
    );
};

/**
 * Hook para acceder al contexto de archivos de registro
 * 
 * @throws Error si se usa fuera del RegistrationFilesProvider
 * 
 * @example
 * ```tsx
 * const { files, setLogoFile } = useRegistrationFiles();
 * 
 * const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
 *   const file = e.target.files?.[0];
 *   if (file) setLogoFile(file);
 * };
 * ```
 */
export const useRegistrationFiles = (): RegistrationFilesContextType => {
    const context = useContext(RegistrationFilesContext);
    
    if (context === undefined) {
        throw new Error('useRegistrationFiles debe ser usado dentro de un RegistrationFilesProvider');
    }
    
    return context;
};
